import { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import sortBy from 'lodash.sortby';
import ComboLocationSelect, { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { Input } from '@components/Input';
import { CHANNEL } from '@constants/channels';
import { useTranslation, useChannels, useLocations } from '@hooks';
import { CAMPAIGN_TYPE } from '@constants/campaigns';
import { StyledSelect } from './styles';

const PrimaryFields = ({ campaignType, isView, isEdit, brands, channels, setChannelType, getContactsNumber }) => {
    const { t } = useTranslation();
    const { control, setValue, watch, selectedCampaign } = useFormContext();
    const { getChannelsByBrand, getChannelsByLocationId, getChannelOptions } = useChannels();
    const { getSelectLocationsOptions } = useLocations();
    const locationType = watch('locationType');
    const brandId = watch('brandId');
    const locationId = watch('locationId');
    const selectedChannelId = watch('channelId');

    const brandsOptions = useMemo(() => brands.map(item => ({ value: item.id, label: item.title })), [brands]);

    const locationsOptions = useMemo(() => getSelectLocationsOptions(), []);

    const channelsOptions = useMemo(() => {
        const availableChannels =
            locationType === LOCATION_TYPE.BRAND ? getChannelsByBrand(brandId) : getChannelsByLocationId(locationId);
        // INFO: withDisconnected to show disconnected channels in options when editing or viewing
        return sortBy(
            getChannelOptions(
                { out: true, withArchivedSelect: isView, withDisconnected: isEdit || isView },
                availableChannels,
            ),
            ['name'],
        );
    }, [locationType, brandId, locationId]);

    useEffect(() => {
        if (campaignType === CAMPAIGN_TYPE.ONETIME && (locationId || brandId) && selectedChannelId && !isView) {
            getContactsNumber();
        }
    }, [locationId, brandId, selectedChannelId]);

    useEffect(() => {
        if (channelsOptions.length > 0 && (locationId || brandId)) {
            /* INFO: if channel is not in options, set the first one when create,
            on edit we need to show disconnected and unlinked channels
            */
            if (!channelsOptions.find(c => c.value === selectedChannelId) && !isEdit && !isView) {
                setValue('channelId', channelsOptions[0]?.value, { shouldValidate: true });
                setChannelType(channelsOptions[0]?.type || CHANNEL.SMS);
            }
        }
    }, [selectedChannelId, channelsOptions, locationId, brandId]);

    return (
        <>
            <Controller
                control={control}
                name="title"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('CampaignsPage.name')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                        disabled={isView}
                        sx={{ gridColumn: '1 / span 2' }}
                    />
                )}
            />
            <ComboLocationSelect
                data={selectedCampaign}
                disabled={isView}
                isView={isView}
                brandsOptions={brandsOptions}
                locationsOptions={locationsOptions}
                updatedLocationCb={() => {
                    setValue('channelId', '', { shouldDirty: true });
                    setValue('whatsAppTemplateId', '');
                    setValue('surveyTemplateId', '', { shouldDirty: true });
                }}
                updatedLocationTypeCb={() => {
                    setValue('channelId', '', { shouldDirty: true });
                    setValue('whatsAppTemplateId', '');
                    setValue('surveyTemplateId', '', { shouldDirty: true });
                }}
            />
            <Controller
                control={control}
                name="channelId"
                rules={{ required: t('validation.required') }}
                render={({ field, fieldState: { error }, ref }) => (
                    <StyledSelect
                        {...field}
                        onChange={({ target: { value } }) => {
                            const channel = channels.find(c => c.id === value);
                            setChannelType(channel?.type || CHANNEL.SMS);
                            setValue('whatsAppTemplateId', '');
                            setValue('channelId', value, { shouldValidate: true, shouldDirty: true });
                        }}
                        ref={ref}
                        label={t('common.channel')}
                        fullWidth
                        options={channelsOptions}
                        required
                        disabled={isView}
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                    />
                )}
            />
        </>
    );
};

export default PrimaryFields;
